import { JobTypeLResponse } from '@doda/api-client/aspida/@types';
import {
  OccupationDisplayListType,
  OccupationListChildrenStateType,
  OccupationListStateType,
} from 'types/occupation';

/**
 * 職種大分類の配列を生成
 * @param masterData マスタデータ
 * @param lIdList 選択された大分類のIDが格納された配列
 * @param occupationMList 中分類の配列
 * @returns 職種の大分類の配列データ
 */
export const createOccupationLargeData = (
  masterData: JobTypeLResponse[],
  lIdList: string[] | undefined,
  occupationMList: OccupationListChildrenStateType[]
): OccupationListStateType[] => {
  const mParentIdList = [
    ...new Set(occupationMList.map((occupationM) => occupationM.parentId)),
  ];

  return masterData.flatMap(({ id, searchJobTypeName, searchJobTypeId }) => {
    const isDisplayValue = lIdList?.includes(searchJobTypeId) ?? false;

    return isDisplayValue || mParentIdList.includes(searchJobTypeId)
      ? [
          {
            id,
            name: searchJobTypeName,
            isChecked: isDisplayValue,
            isDisplay: isDisplayValue,
            searchJobTypeId,
          },
        ]
      : [];
  });
};

/**
 * 職種中分類の配列を生成
 * @param masterData マスタデータ
 * @param mIdList 選択された中分類のIDが格納された配列
 * @param occupationSList 小分類の配列
 * @returns 職種の中分類の配列データ
 */
export const createOccupationMediumData = (
  masterData: JobTypeLResponse[],
  mIdList: string[] | undefined,
  occupationSList: OccupationListChildrenStateType[]
): OccupationListChildrenStateType[] => {
  const sParentIdList = [
    ...new Set(occupationSList.map((occupationS) => occupationS.parentId)),
  ];

  return masterData.flatMap((jobTypeL) =>
    jobTypeL.jobTypeMList.flatMap(
      ({ id, searchJobTypeName, searchJobTypeId }) => {
        const isDisplayValue = mIdList?.includes(searchJobTypeId) ?? false;

        return isDisplayValue || sParentIdList.includes(searchJobTypeId)
          ? [
              {
                id,
                name: searchJobTypeName,
                isChecked: isDisplayValue,
                isDisplay: isDisplayValue,
                searchJobTypeId,
                parentId: jobTypeL.searchJobTypeId,
              },
            ]
          : [];
      }
    )
  );
};

/**
 * 職種小分類の配列を生成
 * @param masterData マスタデータ
 * @param sIdList 選択された小分類のIDが格納された配列
 * @returns 職種の小分類の配列データ
 */
export const createOccupationSmallData = (
  masterData: JobTypeLResponse[],
  sIdList: string[] | undefined
): OccupationListChildrenStateType[] => {
  return masterData.flatMap(({ jobTypeMList }) =>
    jobTypeMList.flatMap((jobTypeM) =>
      jobTypeM.jobTypeSList.flatMap(
        ({ id, searchJobTypeName, searchJobTypeId }) => {
          const isDisplayValue = sIdList?.includes(searchJobTypeId) ?? false;

          return isDisplayValue
            ? [
                {
                  id,
                  name: searchJobTypeName,
                  isChecked: true,
                  isDisplay: true,
                  searchJobTypeId,
                  parentId: jobTypeM.searchJobTypeId,
                },
              ]
            : [];
        }
      )
    )
  );
};

interface CreateOccupationDisplayListType {
  occupationLList: OccupationListStateType[];
  occupationMList: OccupationListChildrenStateType[];
  occupationSList: OccupationListChildrenStateType[];
}
/**
 * 表示用の職種の配列データを作成
 * @param {object} param 職種の生成用の引数
 * @param param.occupationLList 大分類の配列
 * @param param.occupationMList 中分類の配列
 * @param param.occupationSList 小分類の配列
 * @returns 表示用の職種の配列データ
 */
export const createOccupationDisplayList = ({
  occupationLList,
  occupationMList,
  occupationSList,
}: CreateOccupationDisplayListType): OccupationDisplayListType[] => {
  return occupationLList.map((occupationL) => {
    return {
      ...occupationL,
      jobTypeMList: occupationMList
        .filter(
          (occupationM) => occupationM.parentId === occupationL.searchJobTypeId
        )
        .map((occupationM) => {
          return {
            ...occupationM,
            jobTypeSList: occupationSList.filter(
              (occupationS) =>
                occupationS.parentId === occupationM.searchJobTypeId
            ),
          };
        }),
    };
  });
};
